import Phaser from 'phaser';
import * as Bal from '../balance';
import { AttackStyle, EnemyDef, getEnemy } from '../enemies';
import type EnemyBullet from './EnemyBullet';

/** 敌人与世界的接口（由 GameScene 提供） */
export interface EnemyWorld {
  player: Phaser.Physics.Arcade.Sprite & { takeDamage(amount: number): boolean };
  fireBullet(
    x: number, y: number, angle: number, speed: number, damage: number,
    kind: 'straight' | 'homing' | 'lob', life: number, explodeRadius: number, tint: number
  ): void;
  explode(x: number, y: number, radius: number, damage: number): void;
  summon(id: string, x: number, y: number): void;
  slash(x: number, y: number, angle: number, reach: number, tint: number): void;
}

export interface EnemyCtx {
  floor?: number;
  elite?: boolean;
  boss?: boolean;
  affix?: Bal.AffixDef;
}

type ChargeState = 'ready' | 'windup' | 'dash' | 'cool';

export default class Enemy extends Phaser.Physics.Arcade.Sprite {
  hp = 50;
  maxHp = 50;
  speed = 60;
  damage = 10;
  attackRange = 26;
  attackCooldown = 0;
  attackRate = 1000;

  /** 图鉴定义 */
  readonly def: EnemyDef;
  readonly style: AttackStyle;
  readonly flying: boolean;

  isElite = false;
  isBoss = false;
  awake = true;
  affix?: Bal.AffixDef;

  /** 减速状态（冷霜弹） */
  private slowUntil = 0;
  private slowFactor = 1;

  // 冲锋状态机
  private chargeState: ChargeState = 'ready';
  private chargeUntil = 0;
  private chargeCooldownUntil = 0;
  private chargeVx = 0;
  private chargeVy = 0;

  // 自爆引信
  private fuseUntil = 0;
  private fusing = false;
  private detonated = false;

  // 召唤计时
  private nextSummon = 0;

  // 动画
  private currentAnim = '';

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    id: string,
    private readonly world: EnemyWorld,
    ctx: EnemyCtx = {}
  ) {
    super(scene, x, y, getEnemy(id).anims.idle);
    const def = getEnemy(id);
    this.def = def;
    this.style = def.style;
    this.flying = !!def.flying;

    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setCollideWorldBounds(true);

    const floor = ctx.floor ?? 1;
    let hp = def.hp * Bal.enemyHpMul(floor);
    let dmg = def.damage * Bal.enemyDmgMul(floor);
    let spd = def.speed * Bal.enemySpeedMul(floor);

    if (ctx.elite) { hp *= 3; dmg *= 1.4; spd *= 1.05; this.isElite = true; }
    if (ctx.boss) { hp *= Bal.BOSS_HP_MUL; dmg *= Bal.BOSS_DMG_MUL; spd *= Bal.BOSS_SPEED_MUL; this.isBoss = true; }

    // 58 层后词缀效果
    if (ctx.affix) {
      if (ctx.affix.hpMul) hp *= ctx.affix.hpMul;
      if (ctx.affix.speedMul) spd *= ctx.affix.speedMul;
      this.affix = ctx.affix;
    }

    this.maxHp = Math.round(hp);
    this.hp = this.maxHp;
    this.damage = dmg;
    this.speed = spd;
    this.attackRange = def.attackRange;
    this.attackRate = def.attackRate;

    // 缩放（含精英 / BOSS 倍率）
    const sizeMul = this.isBoss ? 1.6 : this.isElite ? 1.3 : 1;
    this.setScale(def.scale * sizeMul);

    // 碰撞体严格依附素材尺寸：def.body 表示「素材在屏幕上的最终像素尺寸」。
    // setSize 接收的是源尺寸，之后会再乘上缩放，因此先除以缩放，
    // 使最终碰撞盒恰好等于素材尺寸（否则 BOSS 这类被放大的单位碰撞会被放大）。
    // 再调用 updateFromGameObject 按 origin 居中（否则机体会偏移半格）。
    const sx = this.scaleX || 1;
    this.body?.setSize(def.body.w / sx, def.body.h / sx);
    if (this.body) (this.body as Phaser.Physics.Arcade.Body).updateFromGameObject();

    // 颜色：精英粉紫 / BOSS 红；飞行单位默认略带透明（有穿墙感）
    if (this.isBoss) this.setTint(0xff6666);
    else if (this.isElite) this.setTint(0xffb0ff);
    else if (def.tint) this.setTint(def.tint);

    // 词缀视觉提示
    if (this.affix) {
      switch (this.affix.type) {
        case 'tanky': this.setTint(0x888888); break;       // 灰色 = 铁甲
        case 'fast': this.setTint(0xffff88); break;        // 黄色 = 疾风
        case 'regenerate': this.setTint(0x88ff88); break;  // 绿色 = 再生
        case 'splitter': this.setTint(0xff88ff); break;    // 粉色 = 分裂
      }
    }

    if (this.flying && !this.isBoss && !this.isElite) this.setAlpha(0.92);

    this.playAnim('idle');
    this.nextSummon = (scene.time.now ?? 0) + (def.params?.interval ?? 5000);
  }

  // ── 动画 ───────────────────────────────────────────────────
  private playAnim(name: 'idle' | 'run' | 'attack' | 'hit') {
    const texKey = this.def.anims[name];
    if (!texKey) return;
    const animKey = `${texKey}-anim`;
    if (this.currentAnim === animKey) return;
    this.currentAnim = animKey;
    if (this.scene.anims.exists(animKey)) this.anims.play(animKey, true);
    else this.setTexture(texKey);
  }

  setAwake(awake: boolean) {
    this.awake = awake;
    if (!awake) (this.body as Phaser.Physics.Arcade.Body)?.setVelocity(0, 0);
  }

  // ── 主循环 ─────────────────────────────────────────────────
  updateAI(time: number, delta: number) {
    if (!this.awake || !this.active) return;
    const p = this.world.player;
    const dist = Phaser.Math.Distance.Between(this.x, this.y, p.x, p.y);
    const ang = Phaser.Math.Angle.Between(this.x, this.y, p.x, p.y);
    const slow = time < this.slowUntil ? this.slowFactor : 1;

    switch (this.style) {
      case 'melee': this.aiMelee(dist, ang, time, delta, slow); break;
      case 'spike': this.aiMelee(dist, ang, time, delta, slow, true); break;
      case 'charge': this.aiCharge(dist, ang, time, delta, slow); break;
      case 'ranged': this.aiShooter(dist, ang, time, delta, slow, 'straight'); break;
      case 'spread': this.aiShootSpread(dist, ang, time, delta, slow); break;
      case 'burst': this.aiBurst(dist, ang, time, delta, slow); break;
      case 'homing': this.aiShooter(dist, ang, time, delta, slow, 'homing'); break;
      case 'lob': this.aiLob(dist, ang, time, delta, slow); break;
      case 'explode': this.aiExplode(dist, ang, time, delta, slow); break;
      case 'summon': this.aiSummon(dist, ang, time, delta, slow); break;
    }

    // 面向玩家
    // 注意：Pixel Adventure 这套怪物素材默认朝【左】（头在左、尾在右），
    // 所以玩家在右侧时必须翻转才会正面朝向玩家。
    this.setFlipX(p.x > this.x);

    // 词缀：再生效果
    if (this.affix?.regenPerSec && this.hp < this.maxHp) {
      this.hp = Math.min(this.maxHp, this.hp + this.affix.regenPerSec * (delta / 1000));
    }
  }

  private setVel(vx: number, vy: number) {
    (this.body as Phaser.Physics.Arcade.Body).setVelocity(vx, vy);
  }

  private moveToward(ang: number, mul: number, delta: number) {
    const s = this.speed * mul * (delta / 16);
    this.setVel(Math.cos(ang) * s, Math.sin(ang) * s);
    if (mul > 0) this.playAnim('run');
  }

  private moveAway(ang: number, mul: number, delta: number) {
    this.moveToward(ang + Math.PI, mul, delta);
  }

  private contactAttack(time: number, dist: number): boolean {
    if (dist > this.attackRange) return false;
    if (time > this.attackCooldown) {
      this.attackCooldown = time + this.attackRate;
      this.world.player.takeDamage(this.damage);
      this.scene.cameras.main.shake(80, 0.004);
      this.scene.tweens.add({ targets: this, scaleX: this.scaleX * 1.12, scaleY: this.scaleY * 1.12, duration: 70, yoyo: true });
      return true;
    }
    return false;
  }

  // ── 各攻击样式 ─────────────────────────────────────────────

  /** 近战 / 甲壳：追到身边接触伤害 */
  private aiMelee(dist: number, ang: number, time: number, delta: number, slow: number, spike = false) {
    if (dist > this.attackRange) {
      this.moveToward(ang, slow, delta);
    } else {
      this.setVel(0, 0);
      this.playAnim(spike && this.def.anims.attack ? 'attack' : 'idle');
      this.contactAttack(time, dist);
    }
  }

  /** 冲锋：接近 → 蓄力 → 冲刺 → 冷却 */
  private aiCharge(dist: number, ang: number, time: number, delta: number, slow: number) {
    const pr = this.def.params ?? {};
    const detect = pr.detect ?? 300;
    const dashMul = pr.dashMul ?? 3;
    const dashMs = pr.dashMs ?? 400;

    if (this.chargeState === 'dash') {
      if (time < this.chargeUntil) {
        this.setVel(this.chargeVx, this.chargeVy);
        this.playAnim('run');
        // 冲刺撞击伤害（旧版漏了这行，导致冲锋完全无伤害）
        this.contactAttack(time, dist);
      } else {
        this.chargeState = 'cool';
        this.chargeCooldownUntil = time + 800;
      }
      return;
    }
    if (this.chargeState === 'cool') {
      this.setVel(0, 0);
      if (time >= this.chargeCooldownUntil) this.chargeState = 'ready';
      return;
    }
    if (this.chargeState === 'windup') {
      this.setVel(0, 0);
      this.playAnim(this.def.anims.attack ? 'attack' : 'idle');
      if (time >= this.chargeUntil) {
        this.chargeState = 'dash';
        this.chargeUntil = time + dashMs;
        const s = this.speed * dashMul * slow;
        this.chargeVx = Math.cos(ang) * s;
        this.chargeVy = Math.sin(ang) * s;
        this.world.slash(this.x, this.y, ang, 60, 0xffcc66);
      }
      return;
    }
    // ready
    if (dist < detect) {
      this.chargeState = 'windup';
      this.chargeUntil = time + 260; // 预警
      this.setVel(0, 0);
      this.setTint(0xffdd88);
      this.scene.time.delayedCall(260, () => { if (this.active && this.def.tint) this.setTint(this.def.tint); });
    } else {
      this.moveToward(ang, slow * 0.7, delta);
    }
  }

  /** 直线 / 追踪射击：拉开距离后开火 */
  private aiShooter(dist: number, ang: number, time: number, delta: number, slow: number, kind: 'straight' | 'homing') {
    const pr = this.def.params ?? {};
    this.kite(dist, ang, delta, slow);
    if (dist <= this.attackRange && time > this.attackCooldown) {
      this.attackCooldown = time + this.attackRate;
      this.playAnim(this.def.anims.attack ? 'attack' : 'idle');
      this.world.fireBullet(
        this.x, this.y, ang, pr.bulletSpeed ?? 240, this.damage, kind,
        pr.bulletLife ?? 3000, 0, 0xff8866
      );
    }
  }

  private aiShootSpread(dist: number, ang: number, time: number, delta: number, slow: number) {
    const pr = this.def.params ?? {};
    this.kite(dist, ang, delta, slow);
    if (dist <= this.attackRange && time > this.attackCooldown) {
      this.attackCooldown = time + this.attackRate;
      this.playAnim(this.def.anims.attack ? 'attack' : 'idle');
      const n = pr.count ?? 3;
      const arc = Phaser.Math.DegToRad(pr.arcDeg ?? 30);
      for (let i = 0; i < n; i++) {
        const a = ang + (i - (n - 1) / 2) * (arc / Math.max(1, n - 1));
        this.world.fireBullet(this.x, this.y, a, pr.bulletSpeed ?? 210, this.damage, 'straight', pr.bulletLife ?? 2600, 0, 0xffdd55);
      }
    }
  }

  private aiBurst(dist: number, ang: number, time: number, delta: number, slow: number) {
    const pr = this.def.params ?? {};
    this.kite(dist, ang, delta, slow);
    if (dist <= this.attackRange && time > this.attackCooldown) {
      this.attackCooldown = time + this.attackRate;
      this.playAnim(this.def.anims.attack ? 'attack' : 'idle');
      const n = pr.count ?? 3;
      const iv = pr.interval ?? 170;
      for (let i = 0; i < n; i++) {
        this.scene.time.delayedCall(i * iv, () => {
          if (!this.active) return;
          const p = this.world.player;
          const a = Phaser.Math.Angle.Between(this.x, this.y, p.x, p.y);
          this.world.fireBullet(this.x, this.y, a, pr.bulletSpeed ?? 260, this.damage, 'straight', pr.bulletLife ?? 2600, 0, 0x88ffcc);
        });
      }
    }
  }

  private aiLob(dist: number, ang: number, time: number, delta: number, slow: number) {
    const pr = this.def.params ?? {};
    this.kite(dist, ang, delta, slow);
    if (dist <= this.attackRange && time > this.attackCooldown) {
      this.attackCooldown = time + this.attackRate;
      this.playAnim(this.def.anims.attack ? 'attack' : 'idle');
      this.world.fireBullet(this.x, this.y, ang, pr.bulletSpeed ?? 240, this.damage, 'lob', 2000, pr.radius ?? 60, 0xffaa44);
    }
  }

  /** 自爆：贴近 → 引信闪烁 → 爆炸 */
  private aiExplode(dist: number, ang: number, time: number, delta: number, slow: number) {
    const pr = this.def.params ?? {};
    if (this.detonated) return;

    if (this.fusing) {
      this.setVel(0, 0);
      if (time >= this.fuseUntil) {
        this.detonated = true;
        this.world.explode(this.x, this.y, pr.radius ?? 80, this.damage * 1.2);
        this.destroy();
      }
      return;
    }
    if (dist <= (pr.detect ?? 110)) {
      this.fusing = true;
      this.fuseUntil = time + (pr.fuseMs ?? 700);
      this.setVel(0, 0);
      this.setTint(0xff4444);
      this.scene.tweens.add({ targets: this, alpha: 0.35, duration: 110, yoyo: true, repeat: -1 });
      return;
    }
    this.moveToward(ang, slow, delta);
  }

  /** 召唤：保持距离并周期召唤小怪 */
  private aiSummon(dist: number, ang: number, time: number, delta: number, slow: number) {
    const pr = this.def.params ?? {};
    this.kite(dist, ang, delta, slow);
    if (time >= this.nextSummon) {
      this.nextSummon = time + (pr.interval ?? 5000);
      this.playAnim(this.def.anims.attack ? 'attack' : 'idle');
      const n = pr.count ?? 1;
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const r = 30 + Math.random() * 24;
        this.world.summon(pr.minion ?? 'chicken', this.x + Math.cos(a) * r, this.y + Math.sin(a) * r);
      }
      this.world.slash(this.x, this.y, ang, 30, 0xaaffaa);
    }
  }

  /** 通用风筝走位：远了靠近、近了后退、适中站定 */
  private kite(dist: number, ang: number, delta: number, slow: number) {
    const pr = this.def.params ?? {};
    const keepMin = pr.keepMin ?? 140;
    const keepMax = pr.keepMax ?? 280;
    if (this.speed <= 0) { this.setVel(0, 0); return; } // 固定炮台
    if (dist > keepMax) this.moveToward(ang, slow, delta);
    else if (dist < keepMin) this.moveAway(ang, slow, delta);
    else { this.setVel(0, 0); this.playAnim('idle'); }
  }

  // ── 受伤 ───────────────────────────────────────────────────
  takeDamage(amount: number, slowAmount = 0) {
    this.hp -= amount;
    if (slowAmount > 0) {
      this.slowUntil = this.scene.time.now + 1200;
      this.slowFactor = 1 - slowAmount;
    }
    // 受击闪烁（BOSS 同样需要反馈，否则没有血条时体感像「打不动」）
    this.scene.tweens.add({ targets: this, alpha: 0.3, duration: 50, yoyo: true, repeat: 1 });
  }
}
